import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { Observable, throwError, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  DegreeListItem,
  PaginatedResponse,
  SearchContext,
  UniversitySearchResult,
} from '../../models/university.models';

@Injectable({
  providedIn: 'root'
})
export class InstitutionService {

  // Flag para usar datos mock o API real
  private useMockData = true;

  // Cache for search filters - loaded only once per session
  private filtersCache: any = null;

  // Cache del contexto del Buscador - se carga una sola vez por sesión.
  private searchContextCache: SearchContext | null = null;

  constructor(private httpClient: HttpClient) { }

  /**
   * Contexto del Buscador de Universidades: si está habilitado para el alumno,
   * si mostrar la nota de corte, país/nivel y categorías permitidas. El back lo
   * gatea por país + nivel. Se cachea por sesión (se limpia en el logout).
   */
  getSearchContext(): Observable<SearchContext> {
    if (this.searchContextCache) {
      return of(this.searchContextCache);
    }

    const url = `${environment.apiUrl}/universities/universities/search-context/`;
    return this.httpClient.get<SearchContext>(url).pipe(
      tap(response => {
        this.searchContextCache = response;
      }),
      catchError(error => throwError(() => error))
    );
  }

  clearContextCache(): void {
    this.searchContextCache = null;
  }

  searchUniversities(filters: any = {}, page = 1): Observable<PaginatedResponse<UniversitySearchResult>> {
    const url = `${environment.apiUrl}/universities/universities/search-by-degree/`;

    let params = new HttpParams();
    params = params.set('page', page.toString());

    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== null && value !== '' && value !== undefined) {
        params = params.set(key, String(value));
      }
    });

    return this.httpClient.get<PaginatedResponse<UniversitySearchResult>>(url, { params }).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /** Búsqueda por lenguaje natural. `filters` son los filtros estructurados que
   *  el usuario tiene puestos en el panel: viajan como query params (igual que en
   *  `searchUniversities`) y el back los interseca con los que interpreta de la
   *  frase, dándole prioridad a estos ante un conflicto. */
  searchUniversitiesNL(query: string, filters: any = {}, page = 1): Observable<any> {
    const url = `${environment.apiUrl}/universities/universities/search-nl/`;

    let params = new HttpParams().set('page', page.toString());

    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== null && value !== '' && value !== undefined) {
        params = params.set(key, String(value));
      }
    });

    return this.httpClient.post(url, { query }, { params }).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  getInstitutionDetail(institutionId: number): Observable<any> {
    const url = `${environment.apiUrl}/universities/universities/${institutionId}/`;
    return this.httpClient.get(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  requestContact(institutionId: number): Observable<any> {
    const url = `${environment.apiUrl}/universities/universities/${institutionId}/request-contact/`;
    return this.httpClient.post(url, {}).pipe(
      catchError(error => throwError(() => error))
    );
  }

  cancelContactRequest(institutionId: number): Observable<any> {
    const url = `${environment.apiUrl}/universities/universities/${institutionId}/request-contact/`;
    return this.httpClient.delete(url).pipe(
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Carreras de una familia dentro de una universidad (grilla del detalle).
   * `university` es requerido (sin él el back devuelve 400). El resultado es
   * paginado; subimos `page_size` para traer toda la oferta de la familia.
   */
  searchDegrees(universityId: number, familyId: number, pageSize = 100): Observable<PaginatedResponse<DegreeListItem>> {
    const url = `${environment.apiUrl}/universities/degrees/search/?university=${universityId}&family=${familyId}&page_size=${pageSize}`;
    return this.httpClient.get<PaginatedResponse<DegreeListItem>>(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  getSearchFilters(): Observable<any> {
    // Return cached filters if already loaded
    if (this.filtersCache) {
      return of(this.filtersCache);
    }

    const url = `${environment.apiUrl}/universities/universities/search-filters/`;
    return this.httpClient.get(url).pipe(
      tap(response => {
        this.filtersCache = response;
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  clearFiltersCache(): void {
    this.filtersCache = null;
  }

  private getMockData(institutionId: number): any {
    // Datos mock basados en el diseño
    const mockInstitutions: Record<number, any> = {
      1: {
        id: 1,
        name: 'ESCUELA SUPERIOR DE GESTIÓN COMERCIAL Y MARKETING (ESIC)',
        titleCard: 'ESIC Business & Marketing School',
        image: '/assets/images/patterns/card_bg6.webp',
        rating: '4.7',
        badge: 'TOP 5\nMejores universidades\nde España',
        locations: ['MADRID', 'SEVILLA', 'BARCELONA', 'ZARAGOZA', 'VALENCIA', 'MÁLAGA', 'VALENCIA', 'MÁLAGA'],
        ownership: 'PRIVADA',
        studyTypes: ['PRESENCIAL', 'ON LINE'],
        sectors: [
          { name: 'SALUD', active: false },
          { name: 'JURÍDICO', active: false },
          { name: 'BUSINESS', active: false },
          { name: 'MARKETING', active: true },
          { name: 'TECNOLOGÍA', active: false },
          { name: 'CONS. PUBLICIDAD Y RRPP', active: false }
        ],
        programs: [
          { name: 'Grado en Marketing' },
          { name: 'Grado en Gestión Comercial y Marketing' },
          { name: 'Bachelor\'s Degree in Marketing Bachelor\'s' },
          { name: 'Bachelor\'s Degree in Marketing and Sales Management' },
          { name: 'Grado Online en Marketing y Comunicación digital' },
          { name: 'Grado en ADE + Marketing' }
        ],
        description: '<span>Dirección: Avenida de Valdenigrales, s/n, 28223 Pozuelo de Alarcón, Madrid\n </span> <br> <span>Perfil del Egresado: Profesional en marketing estratégico, digital, y gestión comercial, con habilidades en análisis de mercado, branding, y liderazgo, preparado para roles en empresas globales.</span>'
      },
      2: {
        id: 2,
        name: 'UNIVERSIDAD CARLOS III DE MADRID (UC3M)',
        titleCard: 'Universidad Carlos III de Madrid (UC3M)',
        image: '/assets/images/patterns/card_bg6.webp',
        rating: '4.5',
        badge: 'TOP 10\nMejores universidades\nde España',
        locations: ['MADRID', 'GETAFE', 'LEGANÉS'],
        ownership: 'PÚBLICA',
        studyTypes: ['PRESENCIAL', 'SEMIPRESENCIAL'],
        sectors: [
          { name: 'INGENIERÍA', active: true },
          { name: 'DERECHO', active: false },
          { name: 'ECONOMÍA', active: true },
          { name: 'CIENCIAS SOCIALES', active: false }
        ],
        programs: [
          { name: 'Grado en Ingeniería Informática' },
          { name: 'Grado en Economía' },
          { name: 'Grado en Derecho' },
          { name: 'Grado en Administración de Empresas' }
        ],
        description: '<span>Dirección: Calle Madrid, 126, 28903 Getafe, Madrid\n </span> <br> <span>Perfil del Egresado: Profesionales con formación técnica y humanística de alto nivel.</span>'
      },
      3: {
        id: 3,
        name: 'UNIVERSIDAD AUTÓNOMA DE MADRID (UAM)',
        titleCard: 'Universidad Autónoma de Madrid (UAM)',
        image: '/assets/images/patterns/card_bg6.webp',
        rating: '4.6',
        badge: 'TOP 3\nMejores universidades\nde España',
        locations: ['MADRID', 'CANTOBLANCO'],
        ownership: 'PÚBLICA',
        studyTypes: ['PRESENCIAL'],
        sectors: [
          { name: 'MEDICINA', active: true },
          { name: 'CIENCIAS', active: true },
          { name: 'FILOSOFÍA', active: false },
          { name: 'PSICOLOGÍA', active: true }
        ],
        programs: [
          { name: 'Grado en Medicina' },
          { name: 'Grado en Biología' },
          { name: 'Grado en Psicología' },
          { name: 'Grado en Física' }
        ],
        description: '<span>Dirección: Ciudad Universitaria de Cantoblanco, 28049 Madrid\n </span> <br> <span>Perfil del Egresado: Profesionales altamente cualificados en áreas de ciencias y humanidades.</span>'
      },
      4: {
        id: 4,
        name: 'UNIVERSIDAD AUTÓNOMA DE ZARAGOZA (ZAR)',
        titleCard: 'Universidad Autónoma de Zaragoza (ZAR)',
        image: '/assets/images/patterns/card_bg6.webp',
        rating: '4.3',
        badge: 'EXCELENCIA\nAcadémica',
        locations: ['ZARAGOZA', 'HUESCA', 'TERUEL'],
        ownership: 'PÚBLICA',
        studyTypes: ['PRESENCIAL', 'ON LINE'],
        sectors: [
          { name: 'INGENIERÍA', active: true },
          { name: 'ARQUITECTURA', active: false },
          { name: 'VETERINARIA', active: true },
          { name: 'EDUCACIÓN', active: false }
        ],
        programs: [
          { name: 'Grado en Ingeniería Mecánica' },
          { name: 'Grado en Veterinaria' },
          { name: 'Grado en Arquitectura' },
          { name: 'Grado en Magisterio' }
        ],
        description: '<span>Dirección: Plaza San Francisco, s/n, 50009 Zaragoza\n </span> <br> <span>Perfil del Egresado: Profesionales con formación integral en diversas áreas del conocimiento.</span>'
      }
    };

    // Retorna la institución específica o la primera por defecto
    return mockInstitutions[institutionId] || mockInstitutions[1];
  }
}
